import { Button } from "@/components/ui/button";
import { ArrowRight, Code2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

const HeroSection = () => {
  const [mounted, setMounted] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Imágenes de fondo del carousel
  const heroImages = [
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop",
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=1080&fit=crop",
    "https://images.unsplash.com/photo-1551434678-e076c223a692?w=1920&h=1080&fit=crop",
    "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1920&h=1080&fit=crop"
  ];

  useEffect(() => {
    setMounted(true);
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay
  useEffect(() => {
    if (!emblaApi) return;

    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 2000);

    return () => clearInterval(interval);
  }, [emblaApi]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Imagen estática para pre-render (react-snap) y primera hidratación */}
      {!mounted && (
        <div className="absolute inset-0">
          <img
            src={heroImages[0]}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="sync"
          />
        </div>
      )}

      {/* Carousel de fondo — solo se monta en el cliente */}
      {mounted && (
        <div className="absolute inset-0" ref={emblaRef}>
          <div className="flex h-full">
            {heroImages.map((image, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0 h-full transition-opacity duration-1000"
                style={{ opacity: selectedIndex === index ? 1 : 0.3 }}
              >
                <img
                  src={image}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "low"}
                  decoding={index === 0 ? "sync" : "async"}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overlay oscuro con gradiente */}
      <div className="" />

      {/* Contenido */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-2 rounded-full mb-6">
            <Code2 className="w-4 h-4" />
            <span className="text-sm font-medium">Soluciones Digitales Profesionales</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
            Transformamos tus
            <span className="text-4xl md:text-6xl lg:text-7xl font-bold text-orange-500 mb-6 leading-tight drop-shadow-2xl block">ideas en realidad</span>
          </h1>

          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-lg">
            Desarrollo de proyectos informáticos con IA(Inteligencia Artificial) a medida. Gerneramos landing pages impactantes
            hasta sistemas empresariales complejos en entorno de alto rendimiento.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#servicios">
              <Button size="lg" className="group shadow-2xl">
                Ver Servicios
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
            <a href="#contacto">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 shadow-xl"
              >
                Contactar
              </Button>
            </a>
          </div>

          {/* Indicadores de carousel */}
          <div className="flex justify-center gap-2 mt-12">
            {heroImages.map((_, index) => (
              <button
                key={index}
                className={`h-1 rounded-full transition-all ${
                  index === selectedIndex
                    ? "bg-white w-12"
                    : "bg-white/30 w-6 hover:bg-white/50"
                }`}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
